import React, { useState, useEffect } from 'react';
import { Search, UserSquare, AlertCircle, CheckSquare, Square } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Funcionalidad {
  id: string;
  nombre: string;
  descripcion: string | null;
  ruta: string;
  icono: string | null;
}

interface Grupo {
  id: string;
  nombre: string;
  descripcion: string | null;
  role: 'administrador' | 'usuario_avanzado' | 'usuario_estandar';
}

interface FuncionalidadGrupo {
  id: string;
  funcionalidad_id: string;
  grupo_id: string;
  created_at: string;
  funcionalidad?: Funcionalidad;
  grupo?: Grupo;
}

export default function FuncionalidadesGrupos() {
  const [funcionalidadesGrupos, setFuncionalidadesGrupos] = useState<FuncionalidadGrupo[]>([]);
  const [grupos, setGrupos] = useState<Grupo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Fetch funcionalidades_grupos
      const { data: permisos, error: permisosError } = await supabase
        .from('funcionalidades_grupos')
        .select(`
          *,
          funcionalidad:funcionalidades(*),
          grupo:grupos_usuarios(*)
        `)
        .order('created_at');

      if (permisosError) throw permisosError;

      // Fetch grupos
      const { data: gruposData, error: gruposError } = await supabase
        .from('grupos_usuarios')
        .select('*')
        .order('nombre');

      if (gruposError) throw gruposError;

      setFuncionalidadesGrupos(permisos || []);
      setGrupos(gruposData || []);
    } catch (error: any) {
      console.error('Error fetching data:', error);
      setError('Error al cargar los datos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePermission = async (funcionalidadGrupo: FuncionalidadGrupo) => {
    try {
      const { error } = await supabase
        .from('funcionalidades_grupos')
        .delete()
        .eq('id', funcionalidadGrupo.id);

      if (error) throw error;

      setSuccess('Permiso actualizado correctamente');
      fetchData();
    } catch (error: any) {
      console.error('Error updating permission:', error);
      setError(error.message);
    }
  };

  const handleAddPermission = async (funcionalidadId: string, grupoId: string) => {
    try {
      const { error } = await supabase
        .from('funcionalidades_grupos')
        .insert([{
          funcionalidad_id: funcionalidadId,
          grupo_id: grupoId
        }]);

      if (error) throw error;

      setSuccess('Permiso añadido correctamente');
      fetchData();
    } catch (error: any) {
      console.error('Error adding permission:', error);
      setError(error.message);
    }
  };

  // Group permissions by functionality
  const groupedPermissions = funcionalidadesGrupos.reduce((acc, curr) => {
    const funcId = curr.funcionalidad_id;
    if (!acc[funcId]) {
      acc[funcId] = {
        funcionalidad: curr.funcionalidad,
        grupos: {}
      };
    }
    if (curr.grupo) {
      acc[funcId].grupos[curr.grupo.id] = curr;
    }
    return acc;
  }, {} as Record<string, {
    funcionalidad?: Funcionalidad;
    grupos: Record<string, FuncionalidadGrupo>;
  }>);

  // Filter based on search term
  const filteredPermissions = Object.values(groupedPermissions).filter(({ funcionalidad }) =>
    funcionalidad?.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    funcionalidad?.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    funcionalidad?.ruta.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Permisos de Grupos</h1>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center mb-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar funcionalidades..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b">
                <th className="pb-3 font-semibold text-gray-600">Funcionalidad</th>
                <th className="pb-3 font-semibold text-gray-600">Ruta</th>
                {grupos.map(grupo => (
                  <th key={grupo.id} className="pb-3 font-semibold text-gray-600">
                    {grupo.nombre}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredPermissions.map(({ funcionalidad, grupos: gruposPermisos }) => (
                <tr key={funcionalidad?.id} className="border-b">
                  <td className="py-3">
                    <div className="flex items-center">
                      <UserSquare className="w-5 h-5 text-blue-500 mr-2" />
                      <div>
                        <div className="font-medium">{funcionalidad?.nombre}</div>
                        {funcionalidad?.descripcion && (
                          <div className="text-sm text-gray-500">{funcionalidad.descripcion}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3">
                    <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                      {funcionalidad?.ruta}
                    </code>
                  </td>
                  {grupos.map(grupo => {
                    const permiso = gruposPermisos[grupo.id];
                    return (
                      <td key={grupo.id} className="py-3">
                        {permiso ? (
                          <button
                            onClick={() => handleTogglePermission(permiso)}
                            className="text-green-500 hover:text-green-600"
                            title="Quitar permiso"
                          >
                            <CheckSquare className="w-5 h-5" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleAddPermission(funcionalidad?.id || '', grupo.id)}
                            className="text-gray-400 hover:text-gray-500"
                            title="Añadir permiso"
                          >
                            <Square className="w-5 h-5" />
                          </button>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {filteredPermissions.length === 0 && (
                <tr>
                  <td colSpan={2 + grupos.length} className="text-center py-8 text-gray-500">
                    <UserSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No se encontraron funcionalidades</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}